import { useMemo } from "react"
import { useOutletContext } from "react-router-dom"

import { formatName } from "@components/Export"
import { useExportAction } from "@components/Table"
import { PROFILE_TYPES_LABELS } from "@components/Domain"

import {
  getCurrencyValue,
  getInvestmentTierAmount,
  getInvestmentTierCurrency
} from "./getInvestedAmount"

const useExport = ({
  getItems,
  sourceProjectId,
  targetProjectId,
  sourceInvestments,
}) => {
  const { getProject, organization } = useOutletContext()
  const sourceProject = getProject(sourceProjectId)
  const targetProject = getProject(targetProjectId)

  const getSourceInvestment = investmentId =>
    sourceInvestments.find(({ id }) => id === investmentId)

  const sourceProjectTierIds = useMemo(() =>
    sourceProject
      ? sourceProject.tiers.map(({ name }) => name)
      : []
  , [ sourceProject ])

  const targetProjectTierIds = useMemo(() =>
    targetProject
      ? targetProject.tiers.map(({ name }) => name)
      : []
  , [ targetProject ])

  const fileName = "transfer_investments"

  const sourceTierColumns = sourceProjectTierIds
    .map(tierId => ({
      title: `Source ${tierId}`,
      value: investment => {
        const { sourceInvestmentId } = investment

        if (sourceInvestmentId) {
          const sourceInvestment = getSourceInvestment(sourceInvestmentId)

          return getInvestmentTierCurrency(tierId, sourceInvestment)
        }

        const amount = getInvestmentTierAmount(tierId, investment)

        if (!amount) {
          return ""
        }

        return getInvestmentTierCurrency(tierId, investment)
      }
    }))

  const targetTierColumns = targetProjectTierIds
    .map(tierId => ({
      title: `Target ${tierId}`,
      value: investment => {
        const { sourceInvestmentId } = investment

        if (!sourceInvestmentId) {
          return getInvestmentTierCurrency(tierId, investment)
        }

        const sourceInvestment = getSourceInvestment(sourceInvestmentId)

        const targetAmount = getInvestmentTierAmount(tierId, investment) || 0
        const sourceAmount = getInvestmentTierAmount(tierId, sourceInvestment) || 0

        const sumAmount = targetAmount + sourceAmount

        if (sumAmount) {
          return getCurrencyValue(sumAmount)
        }

        return ""
      }
    }))

  const columns = [
    {
      title: "Organization ID",
      value: () => organization.id
    },
    {
      title: "Source Project ID",
      value: () => sourceProjectId,
    },
    {
      title: "Source Project Name",
      value: () => getProject(sourceProjectId)?.name,
    },
    {
      title: "Target Project ID",
      value: () => targetProjectId,
    },
    {
      title: "Target Project Name",
      value: () => getProject(targetProjectId)?.name,
    },
    {
      title: "Source Investment ID",
      value: ({ id, sourceInvestmentId }) => sourceInvestmentId || id,
    },
    {
      title: "Target Investment ID",
      value: ({ id, sourceInvestmentId }) => !!sourceInvestmentId ? id : "",
    },
    {
      title: "Investment Profile Name",
      value: ({ name }) => name.trim(),
    },
    {
      title: "Investor Legal Full Name",
      value: ({ investor }) => {
        const { givenName, familyName, additionalName } = investor

        return formatName([ givenName, additionalName, familyName ].join(" "))
      }
    },
    {
      title: "Investment Profile Type",
      value: ({ profileType }) => PROFILE_TYPES_LABELS[profileType] || "",
    },
    ...sourceTierColumns,
    ...targetTierColumns,
  ]

  const onExport = useExportAction(getItems, fileName, columns)

  return onExport
}

export default useExport
